export const SCROLL_TO_SELECTED_DATE_DELAY_MS = 100;
export const FIRST_VISIBLE_DATE_INIT_DELAY_MS = 200;
export const TOOLTIP_DELAY_DURATION_MS = 150;

// Debounce for date navigation buttons (prev/next arrows, today) to prevent
// rapid-fire clicks from flooding scroll animations and API calls.
export const DATE_NAV_DEBOUNCE_MS = 250;

// Debounce between the filter state (chips/URL update instantly) and the
// assignments query key: toggling several filter checkboxes in quick
// succession fires ONE backend request after the user pauses, not one each.
export const PLANNER_FILTERS_REQUEST_DEBOUNCE_MS = 400;

// Failsafe for the post-filter-change scroll restore: when the filter change
// resolves entirely from the query cache, no loading/refresh cycle ever runs,
// so the restore fires after this delay instead. Must exceed
// PLANNER_FILTERS_REQUEST_DEBOUNCE_MS (the reload starts only after the
// debounce) so the failsafe can't race a reload that is still pending.
export const PLANNER_SCROLL_RESTORE_FALLBACK_MS = 700;

// --- Scroll handler timing constants ---

// Throttle interval for wheel event prevention (one frame at 60fps)
export const WHEEL_THROTTLE_MS = 16;

// Scroll idle debounce — fires finalizeHorizontalInteraction after scrolling stops
export const SCROLL_IDLE_DEBOUNCE_MS = 100;

// Grace period after non-wheel (e.g. scrollbar drag) release before normalizing the window
export const NON_WHEEL_RELEASE_GRACE_MS = 250;

// Retry interval when waiting for mouse release during finalize
export const FINALIZE_RETRY_MS = 60;

// Minimum time since last wheel event to classify scroll as non-wheel interaction
export const WHEEL_COOLDOWN_MS = 150;

// Cooldown between successive left/right expand-on-interaction triggers
export const LEFT_EXPAND_COOLDOWN_MS = 180;
export const RIGHT_EXPAND_COOLDOWN_MS = 120;

// Grace period to suppress the scroll handler from re-triggering after programmatic scrollLeft changes
export const PROGRAMMATIC_SCROLL_GRACE_EXPANDED_MS = 75;
export const PROGRAMMATIC_SCROLL_GRACE_COMPENSATION_MS = 250;

// Threshold (days from loaded range edge) for triggering async fetch after arrow-key navigation
export const ARROW_NAV_FETCH_THRESHOLD_DAYS = 14;

// After switching groupBy, suppress vertical pagination briefly to avoid
// loading stale rows while the planner re-renders with the new grouping
export const GROUP_CHANGE_SUPPRESSION_MS = 500;

// --- Planner query cache lifetime ---
//
// The planner's assignment queries are MANUALLY managed: horizontal pagination
// merges many date-range pages into one cache entry, and mutations patch that
// cache in place (zero-refetch — see ASSIGNMENT-CACHE-PATCHING.md). They use
// `staleTime: Infinity` so TanStack Query never auto-refetches.
//
// `gcTime` must match. With the default 5-minute gcTime, leaving the planner
// idle (tab backgrounded, or switching groupBy so a query goes inactive) for
// >5 min garbage-collects the whole merged cache. On return, the query refetches
// from scratch — ONLY the first page for the stored date range — discarding all
// horizontally-paginated pages. Subsequent mutations then patch a thin/partial
// cache, so assignments render in the wrong place and utilization recalculates
// against incomplete data. Infinity keeps the manually-managed cache alive for
// the whole session.
//
// To REPRODUCE the stale-cache bug for testing: temporarily set this to e.g.
// 10_000 (10s), switch the planner to another groupBy tab (Department/Project)
// or background the tab, wait >10s, switch back, then create an assignment —
// it will render incorrectly. Restore to Infinity afterwards.
export const PLANNER_QUERY_GC_TIME_MS = Infinity;

// --- Planner background refresh (keeps shared data fresh without reloading UI) ---
//
// Because the planner cache is kept alive for the whole session (gcTime Infinity)
// and never auto-refetches (staleTime Infinity), a background refresh keeps it in
// sync with other users' changes WITHOUT a loading skeleton, filter reset, or date
// jump — it reuses the post-mutation `refetchActiveAssignmentQueriesInBackground`
// path, which replaces only vertical pages and preserves horizontal extensions.

// Periodic background refresh interval while the planner is active.
export const PLANNER_BACKGROUND_REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// Minimum gap between background refreshes regardless of trigger (focus, mount,
// interval), so a focus event right after the interval doesn't double-fetch.
export const PLANNER_BACKGROUND_REFRESH_MIN_GAP_MS = 60 * 1000; // 1 minute

// Delay before the first background refresh after the planner mounts, so it does
// not collide with the initial data load / first scroll-to-today.
export const PLANNER_BACKGROUND_REFRESH_MOUNT_DELAY_MS = 30 * 1000; // 30 seconds
